<template>
  <pui-big-preview
    ref="bigPreview"
    :getTitlePicto="getTitlePicto"
    :executeConfirmableAction="executeConfirmableAction"
    :executeConfirmableBreakingSave="executeConfirmableBreakingSave"
    :abandonChanges="abandonChanges"
    :valuesAreDirty="isDirty"
    :importInProgress="importExternalFileCount > 0"
    :saveInProgress="saveInProgress"
    @changeZoomedItem="onChangeZoomedItem"
    @closed="onDialogClosed"
    class="edit-poster-dialog"
  >
    <template #name>
      {{ poster | piivoTranslate }}
    </template>
    <template #body>
      <pui-common-spinner
        v-if="globalLoader.messages.length"
        :message="globalLoader.messages[0].message"
        position="fixed"
      />

      <!-- Search dialog -->
      <pui-dialog
        ref="searchDialog"
        :title="
          currentSearchParams != null &&
          currentSearchParams.modes != null &&
          currentSearchParams.modes.length > 0 &&
          currentSearchParams.modes[0] != null &&
          currentSearchParams.modes[0].search != null
            ? piivoTranslateLabel(currentSearchParams.modes[0].search.title)
            : ''
        "
        :showCloseButton="false"
        @close="closeSearchDialog"
        class="edit-poster-search-dialog"
        transition="slide-bottom"
        width="100%"
        fill
      >
        <div slot="dialog-action" class="dialog-action">
          <pui-button :outline="true" :flat="false" @click="closeSearchDialog" variant="primary">
            {{ $t('poster.demands.search.cancel') }}
          </pui-button>
          <pui-button :outline="false" :flat="false" @click="setSearchResult()" variant="primary">
            {{ $t('poster.demands.search.confirm') }}
          </pui-button>
        </div>
        <poster-search-panel
          ref="searchPanel"
          v-if="currentSearchParams"
          :searchConfiguration="currentSearchParams"
          :searchFunction="search"
          :types="valuesTypes"
          :templateId="currentTemplate != null ? currentTemplate.templateId : null"
          @selectedSearchedItemsChanged="updateSelectedSearchedItems"
          @searchResultsValidated="addToSearchSelection"
          @changeSearchMode="selectedSearchMode = $event"
        >
          <template
            v-if="
              currentSearchParams &&
                currentSearchParams &&
                currentSearchParams.multiSelect &&
                currentSearchParams.summary
            "
            #searchSummaryPanel="{ showSearchResultsPanel }"
          >
            <poster-search-summary
              :searchDefinition="currentSearchParams"
              :minimized="showSearchResultsPanel"
              v-model="selectedSearchedItems"
            ></poster-search-summary>
          </template>
        </poster-search-panel>
      </pui-dialog>

      <pui-unsaved-attributes-dialog
        ref="unsavedAttributesDialog"
        :importInProgress="importExternalFileCount > 0"
      />
    </template>
    <template v-if="viewerComponent" #viewer>
      <component
        ref="viewer"
        :is="viewerComponent"
        :posterRequest="posterRequest"
        :poster="poster"
        :fontsError="fontsError"
        :currentTemplate="currentTemplate"
        :values="values"
        :valuesTypes="valuesTypes"
        @reloadFromFontsError="reloadFromFontsError"
      >
      </component>
    </template>
    <template #meta>
      <div class="beaSidePanel">
        <pui-panel>
          <pui-form-panel class="edit-panel">
            <div class="edit-content scroll-body">
              <div v-if="poster" class="bea-variables step1 padding">
                <div class="edit-information">
                  <div class="information-typeformat">
                    <p class="information-type">
                      <span>{{ $t('poster.demands.editing.form.type') }}</span>
                      {{ poster.type | piivoTranslate }}
                    </p>
                    <p class="information-format">
                      <span>{{ $t('poster.demands.editing.form.format') }}</span>
                      {{ poster.format | piivoTranslate }}
                    </p>
                  </div>
                </div>
              </div>
              <div
                v-if="
                  currentForm != null &&
                    currentForm.inputGroups &&
                    currentForm.inputGroups.length > 0
                "
                class="bea-variables step2 padding"
              >
                <pui-attribute-group-panel
                  v-for="group in currentForm.inputGroups"
                  :key="group.itemId"
                  :group="group"
                >
                  <template v-if="group.attributes && values">
                    <pui-attribute-panel
                      v-for="attribute in group.attributes"
                      :ref="`formAttribute_${attribute.alias}`"
                      :key="attribute.itemId"
                      :disabled="true"
                      :attribute="attribute"
                      :value="values[attribute.alias]"
                      :loadOptionsFunction="getPossibleValues"
                      :importExternalFiles="importExternalFiles"
                      @valueChanged="attributeValueChanged(attribute, $event)"
                      @searchRequested="openAttributeSearch($event, true)"
                    />
                  </template>
                </pui-attribute-group-panel>
              </div>
              <div
                v-if="currentForm != null && currentForm.attributeGroups"
                class="bea-variables padding"
              >
                <pui-attribute-group-panel
                  v-for="group in currentForm.attributeGroups"
                  :key="group.itemId"
                  :group="group"
                >
                  <template v-if="group.attributes && values">
                    <pui-attribute-panel
                      v-for="attribute in group.attributes"
                      :ref="`formAttribute_${attribute.alias}`"
                      :key="attribute.itemId"
                      :attribute="attribute"
                      :value="values[attribute.alias]"
                      :loadOptionsFunction="getPossibleValues"
                      :importExternalFiles="importExternalFiles"
                      @valueInput="updateAttributeValue(attribute.alias, $event)"
                      @valueChanged="attributeValueChanged(attribute, $event)"
                      @searchRequested="openAttributeSearch($event, false)"
                    />
                  </template>
                </pui-attribute-group-panel>
              </div>
            </div>
          </pui-form-panel>
        </pui-panel>
      </div>
    </template>
  </pui-big-preview>
</template>

<script>
import RenderManager from 'piivo-poster-engine/src/lib/renderer/renderManager';

import helpers from '../../../../core/helpers';
import services from '../../../../core/services';
import { Loader } from '../../../../utils/loader';
import { AttributeTypes } from '../../../common/constants';
import { iterateForm, updateOn } from '../../../common/helpers/formsHelper';
import logs from '../../api/logs';
import { PosterMimeTypes } from '../../constants';
import ConstantsMixin from '../../mixins/ConstantsMixin';
import { getPosterService } from '../../services';
import { renderThumbnail } from '../../utils/thumbnail';
import { getViewer } from '../viewers';

export default {
  mixins: [ConstantsMixin],
  props: {},
  data() {
    return {
      // Dialog state
      saveInProgress: false,
      isDirty: false,

      posterRequest: null,
      poster: null,
      originalPoster: null,
      currentTemplate: null,

      fontsError: false,
      globalLoader: new Loader(),
      importExternalFileCount: 0,
      editedPosterRequests: [],

      // Search
      currentSearchParams: null,
      selectedSearchMode: null,
      /**
       * Single search result: the selected item from the search results
       * Multi search result: the items in the search summary table
       */
      selectedSearchedItems: [],
    };
  },
  computed: {
    /**
     * Current template (signage) form.
     */
    currentForm() {
      return this.currentTemplate != null ? this.currentTemplate.form : null;
    },
    /**
     * @returns {Map<string, Object>} map of the attribute aliases of the current form
     * to the attribute objects
     */
    attributeMap() {
      if (!this.currentForm) {
        return null;
      }

      const attributeMap = new Map();
      iterateForm(this.currentForm, attribute => {
        attributeMap.set(attribute.alias, attribute);
      });

      return attributeMap;
    },
    /**
     * Current template (signage) bindings types.
     */
    valuesTypes() {
      return this.currentTemplate != null ? this.currentTemplate.types : null;
    },
    /**
     * @returns {object|null} the current values object
     */
    values() {
      return this.currentTemplate ? this.currentTemplate.bindings : null;
    },
    /**
     * @returns {*} the vue viewer component for the poster
     */
    viewerComponent() {
      return this.poster ? getViewer(this.poster.mimeType) : null;
    },
  },
  methods: {
    piivoTranslateLabel(value) {
      return Vue.filter('piivoTranslateLabel')(value);
    },
    // DIALOG
    /**
     * Initializes our state and opens the dialog
     *
     * @param {object[]} posters - the posters that can be zoomed
     * @param {number} posterIndex - the index of the file that should be zoomed
     */
    async open(posters, posterIndex) {
      await this.$nextTick();
      this.$refs.bigPreview.open(posters, posterIndex);
      // Details will be loaded from zoomedItemChange callback
    },
    /**
     * Resets our state and emits the "closed" event
     */
    onEditClosed() {
      this.posterRequest = null;
      this.poster = null;
      this.originalPoster = null;
      this.currentTemplate = null;
      this.isDirty = false;
      const editedPosterRequests = [...this.editedPosterRequests];
      this.editedPosterRequests = [];
      this.$emit('closed', editedPosterRequests);
    },
    onDialogClosed() {
      this.onEditClosed();
    },
    getTitlePicto() {
      // Hardcoded picto for now on purpose from #15518
      return 'mdi-tag';
    },
    /**
     * Reloads the page to recover from a fonts error
     */
    reloadFromFontsError() {
      this.$router.go();
    },
    // ACTION CALLBACKS
    /**
     * Will show a confirmation dialog if the poster is dirty.
     * On save/ignore, the callback will be invoked
     *
     * @param {() => void} actionCallback - the function to call after the user indicates to discard
     * the unsaved changes, or after the changes are saved, or if there is nothing to save at all
     */
    executeConfirmableAction(actionCallback) {
      if (!this.isDirty && this.importExternalFileCount <= 0) {
        actionCallback();
        return;
      }

      this.$refs.unsavedAttributesDialog.open({
        onCancel: () => {
          // Stops action
          if (this.$refs.unsavedAttributesDialog) {
            this.$refs.unsavedAttributesDialog.close();
          }
        },
        onCancelUpdate: async () => {
          // Abandons changes and continues
          this.abandonChanges();
          if (this.$refs.unsavedAttributesDialog) {
            this.$refs.unsavedAttributesDialog.close();
            await this.$nextTick(); // Wait for update dialog to close
          }
          actionCallback();
        },
        onSaveUpdate: async () => {
          // Saves and continues
          try {
            if (this.$refs.unsavedAttributesDialog) {
              this.$refs.unsavedAttributesDialog.close();
              await this.$nextTick(); // Wait for update dialog to close
            }
            await this.executeConfirmableBreakingSave(actionCallback);
          } catch (err) {
            // A save error should've already been handled
          }
        },
      });
    },
    // ACTION CALLBACKS
    /**
     * Saves the poster
     *
     * @param {() => void | Promise<void>} actionCb - called after the save is finished
     * @returns {Promise<void>} resolves once the save and callback finish
     */
    async executeConfirmableBreakingSave(actionCb = null) {
      services.getService('analytics').trackEvent(this.Tracking.LOGS_SAVE_EDITION);
      await this.saveSignage();
      if (actionCb) {
        actionCb();
      }
    },
    /**
     * Discards any changes and reloads the poster template
     * (implicity reloads values since they are on the currentTemplate)
     */
    async abandonChanges() {
      services.getService('analytics').trackEvent(this.Tracking.LOGS_CANCEL_EDITION);

      this.isDirty = false;
      const templates = await getPosterService('signages').convertPosterLogsToTemplate([
        this.originalPoster,
      ]);
      this.currentTemplate = templates.length > 0 ? templates[0] : null;
    },
    /**
     * Triggered when the zoomed item changes
     *
     * @param {object} posterRequest - the new zoomed poster request
     */
    onChangeZoomedItem(posterRequest) {
      if (posterRequest) {
        // Load the new zoomed item
        this.loadDetails(posterRequest, true);
      }
    },
    // EDIT
    async loadDetails(posterRequest) {
      const exitOnError = !this.posterRequest;
      this.posterRequest = posterRequest;

      const removeSpinner = this.globalLoader.addMessage(this.$t('poster.demands.loading.signage'));
      this.fontsError = false;
      try {
        const res = await logs.getSignagesLogs([this.posterRequest.itemId], true);
        this.poster = res.body.length > 0 ? res.body[0] : null;
        this.originalPoster = JSON.parse(JSON.stringify(this.poster));

        if (this.originalPoster.mimeType === PosterMimeTypes.Poster) {
          // For classic posters, load the template
          const templates = await getPosterService('signages').convertPosterLogsToTemplate([
            this.poster,
          ]);
          this.currentTemplate = templates.length > 0 ? templates[0] : null;
        } else {
          this.currentTemplate = null;
        }
        removeSpinner();
      } catch (err) {
        services.getService('alerts').alertError(this.$t('poster.demands.error.load_signage'));
        removeSpinner();

        if (err.fontsError) {
          this.fontsError = true;
        } else if (exitOnError) {
          this.$refs.bigPreview.close();
          this.onEditClosed();
        }
      }
    },
    /**
     * Save the modified signage (edition).
     */
    async saveSignage() {
      if (this.importExternalFileCount) {
        services
          .getService('alerts')
          .alertWarning(this.$t('poster.demands.editing.error.import_external_file_in_progress'));
        return;
      }

      const removeSpinner = this.globalLoader.addMessage(
        this.$t('poster.demands.loading.saving_signage')
      );
      this.saveInProgress = true;

      let thumbnail = null;

      // Only classic posters can be manipulated to generate svg/thumbnail
      if (this.poster.mimeType === PosterMimeTypes.Poster && this.$refs.viewer) {
        // Set showAllPrintables flag to true to have all element in thumbnail
        this.$refs.viewer.setShowAllPrintables(true);
        await this.$nextTick();

        // Update signage SVG and thumbnail
        const svgEl = this.$refs.viewer.getSvgEl();
        if (svgEl) {
          const svgStr = getPosterService('signages').generateSvgFromPreview(
            svgEl,
            this.currentTemplate
          );

          this.currentTemplate.renderContents = [svgStr];

          // Update thumbnail
          thumbnail = await renderThumbnail(svgEl, this.currentTemplate.computedTemplate);
        }
      }

      try {
        await getPosterService('signages').updateSignage(
          this.currentTemplate,
          thumbnail,
          this.values,
          null,
          null
        );
        services.getService('alerts').alertSuccess(this.$t('poster.demands.editing.save_success'));
        if (!this.editedPosterRequests.find(p => p.itemId === this.posterRequest.itemId)) {
          this.editedPosterRequests.push(this.posterRequest);
        }

        // Reload the poster to get its updated values
        const res = await logs.getSignagesLogs([this.posterRequest.itemId], true);
        this.poster = res.body.length > 0 ? res.body[0] : null;
        this.originalPoster = JSON.parse(JSON.stringify(this.poster));
      } catch (err) {
        services.getService('alerts').alertError(this.$t('poster.demands.editing.error.save'));
      }

      removeSpinner();
      this.saveInProgress = false;
      this.isDirty = false;
    },
    /**
     * Update attributes value.
     * @param {String} attributeAlias : Attribute alias
     * @param {Object} newValue : New attribute value
     */
    updateAttributeValue(attributeAlias, newValue) {
      this.setValue(attributeAlias, newValue, true, 'replace');
    },
    /**
     * Update on attributes value changed.
     * @param {object} attribute - the changed attribute
     * @param {Object} newValue - New attribute value
     * @param {Boolean} isInput - Flag indicates if atttribute is a form input or not
     */
    attributeValueChanged(attribute, newValue, isInput) {
      this.updateAttributeValue(attribute.alias, newValue);

      this.startUpdateOn(attribute);
    },
    /**
     * Launches the "updateOn" mechanism for the attribute
     *
     * @param {object} attribute - the attribute whose updateOn to trigger
     */
    startUpdateOn(attribute) {
      const actionCb = cbAttr => {
        if (cbAttr.type === AttributeTypes.LINKS) {
          this.$nextTick(() => {
            const attributePanel = this.$refs[`formAttribute_${cbAttr.alias}`];
            if (attributePanel != null && attributePanel[0] != null) {
              attributePanel[0].loadOptions().catch(() => null);
            }
          });
        }
      };

      // Launch updateOn mechanism for current attribute alias
      updateOn(this.currentForm, attribute, actionCb);
    },
    /**
     * Iterates on the inputGroups and attributeGroups of the current
     * form and executes the callback for the attributes whose aliases
     * match the given alias
     *
     * @param {string} alias - the alias to search for
     * @param {(attribute: object) => void} cb - receives the form attribute object
     */
    doFormAttrCb(alias, cb) {
      iterateForm(this.currentForm, attribute => {
        if (attribute.alias === alias) {
          cb(attribute);
        }
      });
    },
    /**
     * @param {object} attribute - the attribute whose options to retrieve
     * @param {object} parameters - parameters for the load
     * @param {object} parameters.context - additional context to load the values
     * @returns {Promise<object[]>} options for the attribute
     */
    getPossibleValues(attribute, { context }) {
      const allGroups = [
        ...(this.currentForm.inputGroups || []),
        ...(this.currentForm.attributeGroups || []),
      ];

      return getPosterService('values').getPossibleValues(
        attribute,
        context,
        this.currentTemplate != null ? this.currentTemplate.templateId : null,
        bodyParameters =>
          getPosterService('values').addAttributeValuesBodyParameters(
            bodyParameters,
            this.values,
            allGroups
          )
      );
    },
    /**
     * Imports external files to the attribute's storage
     *
     * @param {object} attribute - the attribute onto which we are uploading the files
     * @param {object[]} tempFiles - array of objects containing the file blobs to upload
     * @param {(tempFile, importedFile) => void} onFileUploadedCb - callback for when a file is finished uploading.
     * Receives the original temp file, and the uploaded file
     * @param {(cancelCb: (tempId: string) => void) => void} provideAbortCb - callback
     * that proves the cancel function
     */
    async importExternalFiles(attribute, tempFiles, onFileUploadedCb, provideAbortCb) {
      this.importExternalFileCount++;

      const importedFiles = await getPosterService('posterAttributes').importExternalFiles(
        attribute,
        tempFiles,
        onFileUploadedCb,
        provideAbortCb
      );

      this.importExternalFileCount--;
      return importedFiles;
    },
    /**
     * Update a value for the alias in parameter.
     * @param {String} alias - Object key (property name)
     * @param {Object} value - New value
     * @param {Boolean} tainted - Tainted flag
     * @param {string} updateMode - how to update the value
     */
    setValue(alias, value, tainted, updateMode) {
      // Update is tainted flag
      if (tainted === true) {
        this.isDirty = true;
        this.currentTemplate.isTainted = true;
      }
      const attribute = this.attributeMap ? this.attributeMap.get(alias) : null;
      const typeData = {
        attributeMap: this.attributeMap,
        valuesTypes: this.valuesTypes,
      };
      getPosterService('signages').setPosterValue(
        { values: this.values },
        alias,
        value,
        attribute,
        typeData,
        updateMode
      );

      // When a value changed : search node in template to invalidate (refresh rendering)
      if (this.currentTemplate) {
        RenderManager.searchNodeToInvalidate(
          this.currentTemplate,
          this.values,
          this.currentTemplate.types,
          alias,
          false
        );
      }
    },
    // SEARCH
    /**
     * Open search panel for the attribute in parameter.
     * @param {Object} attribute : The search attribute
     * @param {Boolean} isInput : Flag  that defines if attribute is an input
     */
    async openAttributeSearch(attribute, isInput = false) {
      // Init search panel and open it
      if (!attribute) {
        this.currentSearchParams = null;
        return;
      }

      this.currentSearchParams = attribute.search;

      // Compute summary columns
      if (this.currentSearchParams && this.currentSearchParams.summary) {
        const defaultFormData = await getPosterService('templates').getDefaultFormData();
        helpers
          .getHelper('tableHelper')
          .computeTableColumns(
            this.currentSearchParams.summary.columns,
            defaultFormData != null ? defaultFormData.attributeMap : null
          );
      }

      // Open search panel
      this.$nextTick(() => {
        this.$refs.searchDialog.open();
        this.$nextTick(() => {
          this.$refs.searchPanel.initSearch();
        });
      });
    },
    /**
     * Launch a search with the search value in parameter.
     * @param {Object} searchValues : Object with current search values (key: alias, value: search value)
     * @param {Object} searchValues - Object with current search values (key: alias, value: search value)
     * @param {object} pagination - object with pagination parameters
     * @param {object} pagination.index - page index to request
     * @param {object} pagination.size - page size
     * @param {[sortKey: string, sortDirection: string][]} sorts - sorts entries
     */
    async search(searchValues, pagination, sorts) {
      if (
        !this.currentTemplate ||
        !this.currentTemplate.templateId ||
        !this.selectedSearchMode ||
        !this.selectedSearchMode.search ||
        !this.selectedSearchMode.search.searchSourceId
      ) {
        return [];
      }

      // Search with the values of:
      const parameters = [
        // The inputGroups of the poster form
        {
          attrGroups: this.currentForm != null ? this.currentForm.inputGroups : null,
          values: this.values,
        },
        // The attributeGroups of the search form
        {
          attrGroups: this.selectedSearchMode.search.searchForm
            ? this.selectedSearchMode.search.searchForm.attributeGroups || []
            : [],
          values: searchValues,
        },
      ];

      const results = await getPosterService('values').search(
        this.currentTemplate.templateId,
        this.selectedSearchMode.search.searchSourceId,
        parameters
      );

      return results;
    },
    /**
     * Close the search dialog.
     */
    closeSearchDialog() {
      if (this.$refs.searchDialog != null) {
        this.$refs.searchDialog.close();
      }

      this.selectedSearchedItems = [];
    },
    /**
     * Update selected searched items in searchPanel component.
     * @param {Array} selectedSearchItems: array contains selected items in searchPanel
     * @param {Boolean} isMultiple: true if search mode is multiple
     */
    updateSelectedSearchedItems(selectedSearchItems, isMultiple) {
      if (!isMultiple) {
        this.selectedSearchedItems = selectedSearchItems;
      }
    },
    /**
     * Add selected search results to search selection.
     * @param {Array} selectedSearchResults: array contains selected items in searchPanel
     */
    addToSearchSelection(selectedSearchResults) {
      if (selectedSearchResults && selectedSearchResults.length > 0) {
        this.selectedSearchedItems.push(...selectedSearchResults);
      }
    },
    /**
     * Set search result and close search panel.
     */
    setSearchResult() {
      if (!this.selectedSearchedItems || !this.selectedSearchedItems.length) {
        this.closeSearchDialog();
        return;
      }
      const resultUpdate = getPosterService('signages').mapSearchResultsToValues(
        this.selectedSearchedItems,
        this.selectedSearchMode.search,
        this.currentSearchParams.multiSelect
      );

      Object.entries(resultUpdate).forEach(([alias, update]) => {
        this.setValue(alias, update.value, false, update.updateMode);
        this.doFormAttrCb(alias, attribute => {
          this.startUpdateOn(attribute);
        });
      });

      this.closeSearchDialog();
    },
  },
};
</script>
